/**
 * Drip Gym — Google Ads attribution (Task 3)
 * Standalone storefront module. Logic mirrors scripts/lib/dg-ads-attribution-core.mjs
 */
(function (global) {
  "use strict";

  // Captured during classic script evaluation (defer-safe). DOMContentLoaded
  // clears document.currentScript, which would break data-dry-run reading.
  var SCRIPT_EL = document.currentScript;

  var COOKIE_NAME = "dg_ads_attrib";
  var COOKIE_MAX_AGE_SEC = 7776000;
  var SESSION_FP_KEY = "dg_ads_cart_fp_attached";
  var SNAPSHOT_VERSION = 1;

  var RECOGNIZED_URL_PARAMS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "gclid",
    "gbraid",
    "wbraid",
  ];

  var CART_ATTR_KEYS = {
    utm_source: "__utm_source",
    utm_medium: "__utm_medium",
    utm_campaign: "__utm_campaign",
    utm_term: "__utm_term",
    utm_content: "__utm_content",
    gclid: "__gclid",
    gbraid: "__gbraid",
    wbraid: "__wbraid",
    landing_path: "__landing_path",
    referrer: "__referrer",
    captured_at: "__captured_at",
    fp: "__dg_ads_fp",
  };

  var MAX_LEN = { utm: 256, landing_path: 512, referrer: 512 };
  var CONTROL_RE = /[\0-\x1f\x7f]/g;

  function stripControlChars(value) {
    if (value == null) return "";
    return String(value).replace(CONTROL_RE, "");
  }

  function truncate(value, max) {
    var s = stripControlChars(value);
    return s.length <= max ? s : s.slice(0, max);
  }

  function sanitizeField(key, value) {
    if (key === "landing_path") return truncate(value, MAX_LEN.landing_path);
    if (key === "referrer") return truncate(value, MAX_LEN.referrer);
    if (RECOGNIZED_URL_PARAMS.indexOf(key) !== -1) return truncate(value, MAX_LEN.utm);
    return stripControlChars(value);
  }

  function emptySnapshot() {
    return {
      v: SNAPSHOT_VERSION,
      utm_source: "",
      utm_medium: "",
      utm_campaign: "",
      utm_term: "",
      utm_content: "",
      gclid: "",
      gbraid: "",
      wbraid: "",
      landing_path: "",
      referrer: "",
      captured_at: "",
      fp: "",
    };
  }

  function hasRecognizedAttributionParams(params) {
    for (var i = 0; i < RECOGNIZED_URL_PARAMS.length; i++) {
      var key = RECOGNIZED_URL_PARAMS[i];
      var v = params.get(key);
      if (v != null && String(v).trim() !== "") return true;
    }
    return false;
  }

  function fingerprintPayload(snap) {
    return [
      snap.utm_source,
      snap.utm_medium,
      snap.utm_campaign,
      snap.utm_term,
      snap.utm_content,
      snap.gclid,
      snap.gbraid,
      snap.wbraid,
      snap.landing_path,
      snap.referrer,
      snap.captured_at,
    ].join("\u001f");
  }

  function computeFingerprintFallback(snap) {
    var h = 2166136261;
    var payload = fingerprintPayload(snap);
    for (var i = 0; i < payload.length; i++) {
      h ^= payload.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return "fnv1a-" + (h >>> 0).toString(16);
  }

  function computeFingerprintSync(snap) {
    return computeFingerprintFallback(snap);
  }

  function computeFingerprintAsync(snap) {
    var payload = fingerprintPayload(snap);
    if (global.crypto && global.crypto.subtle && global.TextEncoder) {
      var enc = new TextEncoder().encode(payload);
      return global.crypto.subtle.digest("SHA-256", enc).then(function (buf) {
        var arr = Array.from(new Uint8Array(buf));
        return arr.map(function (b) { return b.toString(16).padStart(2, "0"); }).join("");
      });
    }
    return Promise.resolve(computeFingerprintFallback(snap));
  }

  function buildSnapshotFromUrl(searchString, ctx) {
    ctx = ctx || {};
    var params = new URLSearchParams(
      searchString.charAt(0) === "?" ? searchString.slice(1) : searchString
    );
    if (!hasRecognizedAttributionParams(params)) return null;

    var snap = emptySnapshot();
    for (var i = 0; i < RECOGNIZED_URL_PARAMS.length; i++) {
      var key = RECOGNIZED_URL_PARAMS[i];
      var raw = params.get(key);
      snap[key] = raw != null && String(raw).trim() !== "" ? sanitizeField(key, raw) : "";
    }
    snap.landing_path = sanitizeField("landing_path", ctx.pathname || "");
    snap.referrer = sanitizeField("referrer", ctx.referrer || "");
    snap.captured_at = (ctx.now || new Date()).toISOString();
    return snap;
  }

  function parseCookieSnapshot(cookieValue) {
    if (!cookieValue || typeof cookieValue !== "string") return null;
    try {
      var decoded = decodeURIComponent(cookieValue.trim());
      if (decoded.length > 4096) return null;
      var obj = JSON.parse(decoded);
      if (obj.v !== SNAPSHOT_VERSION) return null;
      var snap = emptySnapshot();
      Object.keys(snap).forEach(function (key) {
        if (key === "v") return;
        if (typeof obj[key] === "string") snap[key] = obj[key];
      });
      return snap;
    } catch (e) {
      return null;
    }
  }

  function readCookie(name) {
    var match = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[$()*+.?[\\\]^{|}]/g, "\\$&") + "=([^;]*)"));
    return match ? match[1] : "";
  }

  function deleteCookie(name) {
    document.cookie = name + "=; Max-Age=0; Path=/; SameSite=Lax";
    if (location.protocol === "https:") {
      document.cookie = name + "=; Max-Age=0; Path=/; SameSite=Lax; Secure";
    }
  }

  function writeCookie(name, value, maxAgeSec) {
    var secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      name + "=" + value + "; Max-Age=" + maxAgeSec + "; Path=/; SameSite=Lax" + secure;
  }

  function serializeCookieSnapshot(snap) {
    var out = {};
    Object.keys(snap).forEach(function (k) { out[k] = snap[k]; });
    out.v = SNAPSHOT_VERSION;
    return encodeURIComponent(JSON.stringify(out));
  }

  function buildCartAttributesFromSnapshot(snap) {
    if (!snap) return null;
    var attributes = {};
    Object.keys(CART_ATTR_KEYS).forEach(function (field) {
      attributes[CART_ATTR_KEYS[field]] = snap[field] != null ? snap[field] : "";
    });
    return { attributes: attributes };
  }

  function buildClearCartAttributesPayload() {
    var attributes = {};
    Object.keys(CART_ATTR_KEYS).forEach(function (field) {
      attributes[CART_ATTR_KEYS[field]] = "";
    });
    return { attributes: attributes };
  }

  function getCartUpdateUrl() {
    var routes = (global.theme && global.theme.routes) || {};
    var cartJs = routes.cart || "/cart.js";
    if (cartJs.indexOf("/cart.js") !== -1) {
      return cartJs.replace("/cart.js", "/cart/update.js");
    }
    if (/\.js$/.test(cartJs)) {
      return cartJs.replace(/\.js$/, "/update.js");
    }
    return "/cart/update.js";
  }

  function getConfig() {
    var script = SCRIPT_EL || document.currentScript;
    var dryRunAttr = script && script.getAttribute("data-dry-run");
    var userCfg = global.DG_ADS_ATTRIBUTION || {};
    var dryRun = userCfg.dryRun;
    if (dryRun == null) {
      if (dryRunAttr == null) dryRun = true;
      else dryRun = dryRunAttr === "true" || dryRunAttr === "1";
    }
    return {
      dryRun: !!dryRun,
      debug: !!userCfg.debug,
    };
  }

  function isCartAttachHttpSuccess(response) {
    if (!response) return false;
    if (response.dryRun === true) return true;
    if (typeof response.ok === "boolean") return response.ok === true;
    if (typeof response.status === "number") {
      return response.status >= 200 && response.status < 300;
    }
    return false;
  }

  function cartAttributesMatchFp(cartAttributes, fp) {
    if (!fp || !cartAttributes || typeof cartAttributes !== "object") return false;
    return (
      cartAttributes.__dg_ads_fp === fp ||
      cartAttributes[CART_ATTR_KEYS.fp] === fp
    );
  }

  function shouldInvalidateSessionAttach(sessionAttachedFp, cookieFp, cart) {
    if (!sessionAttachedFp || !cart) return false;
    var expected = cookieFp || sessionAttachedFp;
    return !cartAttributesMatchFp(cart.attributes, expected);
  }

  function logDebug(cfg) {
    if (!cfg.debug) return;
    // eslint-disable-next-line no-console
    console.log.apply(console, arguments);
  }

  function marketingAllowed() {
    var cp = global.Shopify && global.Shopify.customerPrivacy;
    if (!cp || typeof cp.marketingAllowed !== "function") return false;
    try {
      return cp.marketingAllowed() === true;
    } catch (e) {
      return false;
    }
  }

  var state = {
    pendingSnapshot: null,
    approvedSnapshot: null,
    privacyReady: false,
    attachInFlight: false,
    /** @type {Object<string, Promise<void>>} */
    inflightAttachByFp: {},
    /** @type {Promise<void> | null} */
    promoteInFlight: null,
    cfg: null,
    dryRunPayloads: [],
  };

  function getSessionAttachedFp() {
    try {
      return sessionStorage.getItem(SESSION_FP_KEY) || "";
    } catch (e) {
      return "";
    }
  }

  function setSessionAttachedFp(fp) {
    try {
      if (fp) sessionStorage.setItem(SESSION_FP_KEY, fp);
      else sessionStorage.removeItem(SESSION_FP_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  function clearPostConsentSessionState() {
    setSessionAttachedFp("");
    state.approvedSnapshot = null;
  }

  function loadApprovedFromCookie() {
    var raw = readCookie(COOKIE_NAME);
    if (!raw) return null;
    var snap = parseCookieSnapshot(raw);
    if (!snap) {
      deleteCookie(COOKIE_NAME);
      return null;
    }
    return snap;
  }

  function persistApprovedSnapshot(snap) {
    writeCookie(COOKIE_NAME, serializeCookieSnapshot(snap), COOKIE_MAX_AGE_SEC);
    state.approvedSnapshot = snap;
  }

  function consumeInitialUrlCapture() {
    var boot = global.__DG_ADS_ATTR_INITIAL;
    try {
      delete global.__DG_ADS_ATTR_INITIAL;
    } catch (e) {
      global.__DG_ADS_ATTR_INITIAL = undefined;
    }
    return boot && typeof boot === "object" ? boot : null;
  }

  function resolveUrlInspectionSource(initial, current) {
    current = current || {};
    var cur = {
      search: current.search != null ? String(current.search) : "",
      pathname: current.pathname != null ? String(current.pathname) : "",
      referrer: current.referrer != null ? String(current.referrer) : "",
    };
    if (!initial || typeof initial !== "object") return cur;
    return {
      search:
        typeof initial.search === "string" && initial.search !== ""
          ? initial.search
          : cur.search,
      pathname:
        typeof initial.pathname === "string" && initial.pathname !== ""
          ? initial.pathname
          : cur.pathname,
      referrer:
        typeof initial.referrer === "string" ? initial.referrer : cur.referrer,
    };
  }

  function inspectUrlForPending() {
    var source = resolveUrlInspectionSource(consumeInitialUrlCapture(), {
      search: location.search,
      pathname: location.pathname,
      referrer: document.referrer || "",
    });
    var pending = buildSnapshotFromUrl(source.search, {
      pathname: source.pathname,
      referrer: source.referrer,
      now: new Date(),
    });
    if (pending) state.pendingSnapshot = pending;
  }

  function shouldPostCartUpdate(cookieFp) {
    var sessionFp = getSessionAttachedFp();
    if (!cookieFp) return false;
    if (!sessionFp) return true;
    return sessionFp !== cookieFp;
  }

  function postCartPayload(payload, cfg) {
    if (cfg.dryRun) {
      state.dryRunPayloads.push(JSON.parse(JSON.stringify(payload)));
      logDebug(cfg, "[dg-ads-attribution] dry-run cart payload", payload);
      return Promise.resolve({ dryRun: true, ok: true, status: 200 });
    }
    return fetch(getCartUpdateUrl(), {
      method: "POST",
      credentials: "same-origin",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(payload),
    });
  }

  function attachCartIfNeeded(reason) {
    var cfg = state.cfg;
    if (!marketingAllowed()) return Promise.resolve();

    var snap = state.approvedSnapshot || loadApprovedFromCookie();
    if (!snap || !snap.fp) return Promise.resolve();
    if (!shouldPostCartUpdate(snap.fp)) {
      logDebug(cfg, "[dg-ads-attribution] skip attach", reason, snap.fp);
      return Promise.resolve();
    }

    var fp = snap.fp;
    if (state.inflightAttachByFp[fp]) {
      logDebug(cfg, "[dg-ads-attribution] join inflight attach", reason, fp);
      return state.inflightAttachByFp[fp];
    }

    var payload = buildCartAttributesFromSnapshot(snap);
    if (!payload) return Promise.resolve();

    state.attachInFlight = true;
    var run = postCartPayload(payload, cfg)
      .then(function (res) {
        if (!isCartAttachHttpSuccess(res)) {
          var status = res && typeof res.status === "number" ? res.status : "unknown";
          throw new Error("cart-attach-http-" + status);
        }
        setSessionAttachedFp(fp);
      })
      .catch(function () {
        /* retryable — session fp stays empty / uncleared on HTTP failure */
      })
      .finally(function () {
        delete state.inflightAttachByFp[fp];
        state.attachInFlight = Object.keys(state.inflightAttachByFp).length > 0;
      });
    state.inflightAttachByFp[fp] = run;
    logDebug(cfg, "[dg-ads-attribution] attach start", reason, fp);
    return run;
  }

  function invalidateSessionIfCartMissingFp(cart) {
    var snap = state.approvedSnapshot || loadApprovedFromCookie();
    var cookieFp = snap && snap.fp ? snap.fp : "";
    var sessionFp = getSessionAttachedFp();
    if (shouldInvalidateSessionAttach(sessionFp, cookieFp, cart)) {
      setSessionAttachedFp("");
      return true;
    }
    return false;
  }

  function handleRevocation() {
    deleteCookie(COOKIE_NAME);
    state.pendingSnapshot = null;
    clearPostConsentSessionState();
    var clearPayload = buildClearCartAttributesPayload();
    var cfg = state.cfg || getConfig();
    if (cfg.dryRun) {
      state.dryRunPayloads.push(JSON.parse(JSON.stringify(clearPayload)));
      return;
    }
    postCartPayload(clearPayload, cfg);
  }

  function promotePendingIfAllowed() {
    if (!marketingAllowed()) return Promise.resolve();
    if (state.promoteInFlight) return state.promoteInFlight;

    state.promoteInFlight = Promise.resolve().then(function () {
      var base = state.pendingSnapshot || state.approvedSnapshot || loadApprovedFromCookie();
      if (!base) return;

      return computeFingerprintAsync(base).then(function (fp) {
        base.fp = fp;
        persistApprovedSnapshot(base);
        state.pendingSnapshot = null;
        return attachCartIfNeeded("consent-promotion");
      });
    }).finally(function () {
      state.promoteInFlight = null;
    });

    return state.promoteInFlight;
  }

  function onPrivacyReady() {
    state.privacyReady = true;
    if (!marketingAllowed()) {
      if (state.approvedSnapshot || readCookie(COOKIE_NAME)) {
        handleRevocation();
      }
      return;
    }

    state.approvedSnapshot = loadApprovedFromCookie();
    return promotePendingIfAllowed();
  }

  function initPrivacyApi() {
    if (global.Shopify && global.Shopify.customerPrivacy) {
      onPrivacyReady();
      document.addEventListener("visitorConsentCollected", function () {
        if (!marketingAllowed()) {
          handleRevocation();
          return;
        }
        promotePendingIfAllowed();
      });
      return;
    }

    if (!global.Shopify || typeof global.Shopify.loadFeatures !== "function") {
      return;
    }

    global.Shopify.loadFeatures(
      [{ name: "consent-tracking-api", version: "0.1" }],
      function (error) {
        if (error) return;
        document.addEventListener("visitorConsentCollected", function () {
          if (!marketingAllowed()) {
            handleRevocation();
            return;
          }
          promotePendingIfAllowed();
        });
        onPrivacyReady();
      }
    );
  }

  function bindCartLifecycle() {
    document.addEventListener("ajaxProduct:added", function () {
      // Empty→nonempty cart add can wipe attributes while session fp remains set.
      // Clear dedupe so reattach runs; cart:updated still invalidates from cart payload.
      if (marketingAllowed()) {
        var snap = state.approvedSnapshot || loadApprovedFromCookie();
        if (snap && snap.fp) setSessionAttachedFp("");
      }
      attachCartIfNeeded("ajaxProduct:added");
    });
    document.addEventListener("cart:updated", function (evt) {
      var cart = evt && evt.detail && evt.detail.cart;
      if (cart) invalidateSessionIfCartMissingFp(cart);
      attachCartIfNeeded("cart:updated");
    });
    document.addEventListener("dg-ads:reattach", function () {
      setSessionAttachedFp("");
      attachCartIfNeeded("dg-ads:reattach");
    });
    // bfcache / return navigation: retry if prior attach never landed
    global.addEventListener("pageshow", function () {
      if (!marketingAllowed()) return;
      attachCartIfNeeded("pageshow");
    });
  }

  function init() {
    state.cfg = getConfig();
    inspectUrlForPending();
    initPrivacyApi();
    bindCartLifecycle();

    global.DgAdsAttribution = {
      getState: function () {
        return {
          pending: state.pendingSnapshot,
          approved: state.approvedSnapshot,
          marketingAllowed: marketingAllowed(),
          dryRunPayloads: state.dryRunPayloads.slice(),
        };
      },
      buildClearCartAttributesPayload: buildClearCartAttributesPayload,
      buildCartAttributesFromSnapshot: buildCartAttributesFromSnapshot,
      PRE_CONSENT_LIMITATION:
        "Pending attribution is memory-only until marketingAllowed(); lost on navigation before consent.",
    };
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})(window);
